/**
 * La serie mensual editable: reemplazo directo de la hoja "Revenue" del sheet, y
 * pantalla de carga hasta que Stripe escriba por la ingesta.
 *
 * Dos decisiones que se ven al usarla:
 *
 *  - **El growth se recalcula mientras escribís**, con las funciones del
 *    contrato, y publica su base al lado. Editar julio cambia el growth de julio
 *    y el de agosto: por eso el recálculo es de la serie y no de la celda.
 *  - **Guardar pasa por un diálogo** que lista lo que va a cambiar, viejo → nuevo.
 *    Es una escritura sobre el número del que cuelga todo el panel; que se pueda
 *    hacer sin ver el diff es cómo el sheet terminó con cifras que no cierran.
 *    Si el PUT falla, el diálogo no se cierra y ofrece Reintentar.
 */
import {
  formatMetricValue,
  formatPeriodo,
  formatPeriodoCorto,
  type RevenueMonth,
} from '@ops/contracts'
import { useMemo, useState } from 'react'

import { mensajeDeError } from '../../lib/api'
import { useUpsertRevenueMonth } from '../../lib/queries'
import {
  Button,
  DataTable,
  DeltaTag,
  Modal,
  Panel,
  ProvenanceBadge,
  Stack,
  StatusPill,
  type Column,
} from '../../ui'
import {
  CAMPOS_CONTEO,
  CAMPOS_EDITABLES,
  CAMPO_LABEL,
  aplicarEdiciones,
  celdasInvalidas,
  edicionesDe,
  periodosEditados,
  textoDeCelda,
  upsertsDe,
  valorDe,
  type Borrador,
  type CampoEditable,
} from './ediciones'

export interface TablaMensualProps {
  meses: readonly RevenueMonth[]
  /** Período del header: se marca la fila para no editar el mes equivocado. */
  periodoActual: string
  /** Nombre de quien edita, para decir con qué firma queda la carga. */
  autor: string | null
  now: Date
  onGuardado: (cantidad: number) => void
}

function formatearCelda(valor: number | null, campo: CampoEditable): string {
  if (valor === null) return ''
  return CAMPOS_CONTEO.includes(campo)
    ? formatMetricValue(valor, 'count')
    : formatMetricValue(valor, 'usd')
}

export function TablaMensual({
  meses,
  periodoActual,
  autor,
  now,
  onGuardado,
}: TablaMensualProps) {
  const [borrador, setBorrador] = useState<Borrador>({})
  const [confirmando, setConfirmando] = useState(false)
  const mutacion = useUpsertRevenueMonth()

  const ediciones = useMemo(() => edicionesDe(meses, borrador), [meses, borrador])
  const filas = useMemo(() => aplicarEdiciones(meses, ediciones), [meses, ediciones])
  const editados = useMemo(() => new Set(periodosEditados(ediciones)), [ediciones])
  const invalidas = useMemo(() => celdasInvalidas(borrador), [borrador])
  const originalDe = useMemo(
    () => new Map(meses.map((m) => [m.periodo, m])),
    [meses],
  )

  function escribir(periodo: string, campo: CampoEditable, texto: string) {
    setBorrador((actual) => ({
      ...actual,
      [periodo]: { ...actual[periodo], [campo]: texto },
    }))
  }

  function descartar() {
    setBorrador({})
  }

  async function guardar() {
    const cambios = upsertsDe(filas, ediciones)
    try {
      // Secuencial y no en paralelo: el endpoint es por período y si uno falla
      // queremos saber cuál, no perder el error entre cuatro promesas.
      for (const cambio of cambios) {
        await mutacion.mutateAsync(cambio)
      }
      setBorrador({})
      setConfirmando(false)
      onGuardado(cambios.length)
    } catch {
      // El diálogo queda abierto mostrando el mensaje de la API y ofreciendo
      // Reintentar con los valores intactos. Un mes a medio guardar sin aviso es
      // exactamente el problema que tenía el sheet.
    }
  }

  const columnaMes: Column<RevenueMonth> = {
    key: 'periodo',
    header: 'Mes',
    rowHeader: true,
    width: '132px',
    sortValue: (mes) => mes.periodo,
    filterValue: (mes) => `${mes.periodo} ${formatPeriodo(mes.periodo)}`,
    render: (mes) => (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {formatPeriodo(mes.periodo)}
        {mes.periodo === periodoActual ? (
          <StatusPill size="sm" severidad="unknown" icono={false}>
            período abierto
          </StatusPill>
        ) : null}
        {editados.has(mes.periodo) ? (
          <StatusPill size="sm" severidad="warning" testId="pill-sin-guardar">
            sin guardar
          </StatusPill>
        ) : null}
      </span>
    ),
  }

  const columnasEditables: Column<RevenueMonth>[] = CAMPOS_EDITABLES.map(
    (campo) => ({
      key: campo,
      header: CAMPO_LABEL[campo],
      align: 'right',
      width: campo === 'mrr' ? '124px' : '104px',
      sortValue: (mes) => valorDe(mes, campo),
      render: (mes) => {
        const clave = `${mes.periodo}:${campo}`
        const invalida = invalidas.includes(clave)
        const original = originalDe.get(mes.periodo)
        const cambiada =
          original !== undefined && valorDe(original, campo) !== valorDe(mes, campo)

        return (
          <input
            type="text"
            inputMode="decimal"
            aria-label={`${CAMPO_LABEL[campo]} de ${formatPeriodo(mes.periodo)}`}
            aria-invalid={invalida ? true : undefined}
            value={textoDeCelda(mes, campo, borrador, (valor) =>
              formatearCelda(valor, campo),
            )}
            onChange={(evento) =>
              escribir(mes.periodo, campo, evento.target.value)
            }
            placeholder="sin dato"
            className={[
              'h-6 w-full rounded border bg-n100 px-1.5 text-right text-meta tabular text-text',
              'hover:border-n300 focus:bg-surface placeholder:text-muted',
              invalida
                ? 'border-danger bg-danger-soft'
                : cambiada
                  ? 'border-warning bg-warning-soft'
                  : 'border-transparent',
            ].join(' ')}
          />
        )
      },
    }),
  )

  const columnasDerivadas: Column<RevenueMonth>[] = [
    {
      key: 'netNewMrr',
      header: 'Net new MRR',
      align: 'right',
      width: '118px',
      sortValue: (mes) => mes.netNewMrr,
      render: (mes) =>
        mes.netNewMrr === null ? (
          <span className="text-micro text-muted-strong">
            faltan sus tres partes
          </span>
        ) : (
          <span className="tabular font-medium text-text">
            {formatMetricValue(mes.netNewMrr, 'usd')}
          </span>
        ),
    },
    {
      key: 'growthMoMPct',
      header: 'Growth MoM',
      align: 'right',
      width: '210px',
      sortValue: (mes) => mes.growthMoMPct,
      render: (mes) =>
        mes.growthMoMPct === null ? (
          <span className="text-micro text-muted-strong">
            {mes.mrr === null ? 'sin MRR del mes' : 'sin mes anterior'}
          </span>
        ) : (
          <DeltaTag
            deltaPct={mes.growthMoMPct}
            base={
              mes.periodoBase === null
                ? null
                : { periodo: mes.periodoBase, valor: mes.mrrBase }
            }
            unidad="usd"
            direccion="higher_better"
            size="sm"
            className="justify-end"
          />
        ),
    },
    {
      key: 'provenance',
      header: 'Proveniencia',
      width: '210px',
      filterValue: (mes) => mes.provenance.source,
      render: (mes) => (
        <ProvenanceBadge
          provenance={mes.provenance}
          now={now}
          hrefAdmin="/admin"
        />
      ),
    },
  ]

  const columnas = [columnaMes, ...columnasEditables, ...columnasDerivadas]
  const cantidadEditada = editados.size
  const hayInvalidas = invalidas.length > 0

  const totalNetNew = filas.reduce(
    (acc, mes) => (mes.netNewMrr === null ? acc : acc + mes.netNewMrr),
    0,
  )
  const cambios = upsertsDe(filas, ediciones)

  return (
    <>
      <Panel
        titulo="Serie mensual"
        subtitulo="Las seis primeras columnas se editan acá. Net new MRR y growth MoM los calcula el panel."
        padding="none"
        acciones={
          <Stack direccion="horizontal" gap="sm" align="center" wrap>
            {cantidadEditada > 0 ? (
              <span
                data-testid="tabla-mensual-pendientes"
                className="text-micro font-semibold text-warning-text"
              >
                {cantidadEditada === 1
                  ? '1 mes editado sin guardar'
                  : `${cantidadEditada} meses editados sin guardar`}
              </span>
            ) : null}
            {hayInvalidas ? (
              <span
                role="alert"
                className="text-micro font-semibold text-danger-text"
              >
                {invalidas.length === 1
                  ? '1 celda no es un número'
                  : `${invalidas.length} celdas no son números`}
              </span>
            ) : null}
            <Button
              variante="sutil"
              onClick={descartar}
              disabled={cantidadEditada === 0 && !hayInvalidas}
            >
              Descartar mis ediciones
            </Button>
            <Button
              variante="primario"
              icono="lapiz"
              onClick={() => setConfirmando(true)}
              disabled={cantidadEditada === 0 || hayInvalidas}
            >
              Guardar cambios
            </Button>
          </Stack>
        }
        footer={
          <span>
            Net new MRR acumulado de la serie:{' '}
            <b className="font-semibold text-text tabular">
              {formatMetricValue(totalNetNew, 'usd')}
            </b>
            . El MRR de {formatPeriodo(periodoActual)} sale de las suscripciones
            activas:{' '}
            <a
              href="#revenue-suscripciones"
              className="font-semibold text-accent-text underline underline-offset-2"
            >
              cada una abre su registro en Stripe
            </a>
            .
          </span>
        }
      >
        <DataTable
          columns={columnas}
          rows={filas}
          rowKey={(mes) => mes.periodo}
          rowSeverity={(mes) => (mes.mrr === null ? 'unknown' : 'ok')}
          density="compacta"
          ordenInicial={{ key: 'periodo', dir: 'desc' }}
          emptyMessage="No hay meses cargados todavía. Cargá el primero editando la fila del período abierto."
          caption={
            <>
              Serie mensual de MRR y movimientos. Las columnas editables se
              guardan como carga manual
              {autor === null ? '' : `, firmada por ${autor}`}, y vencen a los 30
              días.
            </>
          }
        />
      </Panel>

      <Modal
        open={confirmando}
        onClose={() => setConfirmando(false)}
        title={
          cambios.length === 1
            ? 'Guardar 1 mes'
            : `Guardar ${cambios.length} meses`
        }
        subtitle="Queda como carga manual, con tu nombre y la fecha de hoy. Se sobrescribe lo que haya escrito Stripe."
        error={mutacion.isError ? mensajeDeError(mutacion.error) : null}
        onRetry={mutacion.isError ? () => void guardar() : undefined}
        reintentando={mutacion.isPending}
        footer={
          <>
            <Button variante="secundario" onClick={() => setConfirmando(false)}>
              Cancelar
            </Button>
            <Button
              variante="primario"
              onClick={() => void guardar()}
              cargando={mutacion.isPending}
            >
              Guardar
            </Button>
          </>
        }
      >
        <ul className="flex list-none flex-col gap-2 p-0">
          {cambios.map((cambio) => {
            const original = originalDe.get(cambio.periodo)
            return (
              <li key={cambio.periodo} className="text-meta">
                <span className="font-semibold text-text">
                  {formatPeriodo(cambio.periodo)}
                </span>
                <ul className="mt-1 flex list-none flex-col gap-0.5 p-0 text-micro text-muted-strong">
                  {CAMPOS_EDITABLES.filter(
                    (campo) =>
                      original !== undefined &&
                      valorDe(original, campo) !== (cambio[campo] ?? null),
                  ).map((campo) => (
                    <li key={campo} className="tabular">
                      {CAMPO_LABEL[campo]}:{' '}
                      {original === undefined
                        ? 'sin dato'
                        : (formatearCelda(valorDe(original, campo), campo) ||
                          'sin dato')}{' '}
                      → <b className="font-semibold text-text">
                        {formatearCelda(cambio[campo], campo) || 'sin dato'}
                      </b>
                    </li>
                  ))}
                </ul>
              </li>
            )
          })}
        </ul>
        <p className="text-micro text-muted-strong">
          El growth MoM de {formatPeriodoCorto(periodoActual)} y del mes
          siguiente se recalculan solos: no se guardan, se derivan del MRR.
        </p>
      </Modal>
    </>
  )
}
